import { getNarrativeViewModel } from "../helpers/decisionNarrativeHelpers";
import { CurrentBaseline } from "./narrative/CurrentBaseline";
import { CurrentProtectionBaseline } from "./narrative/CurrentProtectionBaseline";
import { ExecutiveActions } from "./narrative/ExecutiveActions";
import { ExposureGap } from "./narrative/ExposureGap";
import { ProjectedPortfolioPosition } from "./narrative/ProjectedPortfolioPosition";
import { ManagementDecisionAlternatives } from "./narrative/ManagementDecisionAlternatives";
import { RecommendedCapitalAllocation } from "./narrative/RecommendedCapitalAllocation";
import PortfolioExecutiveConclusion from "./PortfolioExecutiveConclusion";
import { UnresolvedPortfolioExposure } from "./narrative/UnresolvedPortfolioExposure";
import AnalyticalGovernanceHandoff from "./AnalyticalGovernanceHandoff";

export const PortfolioEvaluationContext = ({ decision, activeScenarios, currentPortfolioPosition, settings, capitalRequirementReference, investmentConstraints }) => {
  const narrative = getNarrativeViewModel(decision, activeScenarios, settings, currentPortfolioPosition);
  return (
    <>
      <CurrentProtectionBaseline {...narrative} />
      <ExposureGap {...narrative} />
      {capitalRequirementReference}
      {investmentConstraints}
    </>
  );
};

export const PortfolioDecisionNarrative = ({ decision, activeScenarios, currentPortfolioPosition, settings, capitalRequirementReference, investmentConstraints, capitalAllocationBrief, handoffStatus, handoffVisible = false, onRetryHandoffPersistence, onContinueToPlanning, handoffError }) => {
  const narrative = getNarrativeViewModel(decision, activeScenarios, settings, currentPortfolioPosition);
  const executiveActions = narrative.selectedActions.map((action) => {
    const scenario = activeScenarios?.find(
      (candidate) => String(candidate?.id ?? candidate?.scenario_id) === String(action?.scenario_id)
    );

    return {
      ...action,
      executiveScenarioName:
        action?.scenarioName ||
        action?.scenario_name ||
        action?.risk_scenario_name ||
        scenario?.name ||
        scenario?.title ||
        action?.asset_name ||
        action?.name ||
        "Risk scenario",
    };
  });
  if (!decision) {
    return (
      <div className="space-y-6" aria-label="Capital Allocation Evaluation Preparation">
        <div className="border-b border-slate-200 pb-4">
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Evaluation preparation</p>
          <h2 className="mt-2 text-xl font-semibold text-slate-950">Prepare the Capital Allocation Evaluation</h2>
          <p className="mt-1 text-sm text-slate-600">Review the current position, protection baseline, optimizer scope and economic reference before setting available capital.</p>
        </div>
        <CurrentBaseline {...narrative} />
        <CurrentProtectionBaseline {...narrative} />
        <ExposureGap {...narrative} />
        {capitalRequirementReference}
        {investmentConstraints}
        {handoffVisible && <AnalyticalGovernanceHandoff handoffStatus={handoffStatus} onRetryPersistence={onRetryHandoffPersistence} onContinue={onContinueToPlanning} handoffError={handoffError} />}
      </div>
    );
  }

  return (
    <div className="space-y-6" aria-label="Management Capital Allocation Decision Narrative">
      <div className="border-b border-slate-200 pb-4">
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Level 1 · Principal decision flow</p>
        <h2 className="mt-2 text-xl font-semibold text-slate-950">Management Decision Context</h2>
        <p className="mt-1 text-sm text-slate-600">Review the analytical recommendation, modeled effect and remaining exposure before continuing to formal Investment Planning.</p>
      </div>
      <div className="grid gap-6 xl:grid-cols-2">
        <CurrentBaseline {...narrative} />
        <RecommendedCapitalAllocation {...narrative} selectedActions={executiveActions} />
      </div>
      {narrative.feasible && (
        <>
          <PortfolioExecutiveConclusion decision={decision} settings={settings} />
        </>
      )}
      {capitalAllocationBrief}
      <ManagementDecisionAlternatives alternatives={narrative.alternatives} />
      {narrative.feasible && <UnresolvedPortfolioExposure {...narrative} />}
      <ProjectedPortfolioPosition {...narrative} />
      {narrative.feasible && <AnalyticalGovernanceHandoff handoffStatus={handoffStatus} onRetryPersistence={onRetryHandoffPersistence} onContinue={onContinueToPlanning} handoffError={handoffError} />}
    </div>
  );
};

export const PortfolioExecutiveActions = ExecutiveActions;
